import escapeHtml from 'escape-html';
import { Text, Descendant } from 'slate';
import { NON_BREAKING_SPACE_CODE } from '@sdk/common/constants';

import { CustomText } from '../types';

import { isElementVoid } from './utils';

export default function serialize(
  node: Descendant | Descendant[],
  prevNode?: Descendant | null
): string {
  if (Array.isArray(node)) {
    return mapChildren(node);
  }

  if (Text.isText(node)) {
    return mapTextNode(node);
  }

  const children = mapChildren(node.children);
  let prefix = '';
  const blockPrefix = prevNode ? '\n\n' : '';
  switch (node.type) {
    case 'paragraph': {
      if (!Text.isText(prevNode) && prevNode?.type) {
        prefix = '\n\n';
      }

      return `${prefix}${isEmptyNode(node) ? `${NON_BREAKING_SPACE_CODE}` : children}`;
    }
    case 'block-quote': {
      const childrenWithEmptyRows = children
        .split('\n')
        .map((line) => {
          return line.trim() ? `> ${line}` : `>${NON_BREAKING_SPACE_CODE}`;
        })
        .join('\n');

      return `${blockPrefix}${childrenWithEmptyRows}`;
    }
    case 'heading-1':
      return `${blockPrefix}# ${children}`;
    case 'heading-2':
      return `${blockPrefix}## ${children}`;
    case 'heading-3':
      return `${blockPrefix}### ${children}`;
    case 'link': {
      if (node.link === children) {
        return children;
      }

      return children ? `[${children}](${escapeHtml(node.link)})` : '';
    }
    case 'mention':
      return `@${node.displayName}[${node.personaId}]`;
    case 'code-block': {
      const blockCodeSymbol = '```';
      return children ? `${blockPrefix}${blockCodeSymbol}\n${children}\n${blockCodeSymbol}` : '';
    }
    default:
      return children;
  }
}

function mapTextNode(node: CustomText): string {
  let string = node.text;

  if (!string) {
    return '';
  }

  if (node.underline) {
    string = wrapInlineMarkdown(string, '__');
  }

  if (node.strikethrough) {
    string = wrapInlineMarkdown(string, '~~');
  }

  if (node.bold) {
    string = wrapInlineMarkdown(string, '**');
  }

  if (node.italic) {
    string = wrapInlineMarkdown(string, '*');
  }

  if (node.code) {
    string = wrapInlineMarkdown(string, '`');
  }

  return string;
}

function wrapInlineMarkdown(string: string, markdownChar: string): string {
  let endingSpacesCount = 0;
  let startingSpacesCount = 0;

  let wrappedString = string?.replace(/\s+$/g, (m) => {
    endingSpacesCount = m.length;
    return '';
  });

  wrappedString = wrappedString?.replace(/^\s+/g, (m) => {
    startingSpacesCount = m.length;
    return '';
  });

  return `${Array(startingSpacesCount + 1).join(
    ' '
  )}${markdownChar}${wrappedString}${markdownChar}${Array(endingSpacesCount + 1).join(' ')}`;
}

function isEmptyNode(node: Descendant): boolean {
  if (Text.isText(node)) {
    return isEmptyText(node.text);
  }

  return !isElementVoid(node) && node.children.every((c) => isEmptyNode(c));
}

function isEmptyText(text: string): boolean {
  return text.trim() === '';
}

function mapChildren(children: Descendant[]): string {
  return children?.map((n, i) => serialize(n, i > 0 ? children[i - 1] : null)).join('');
}
